import { useMutation, useQueryClient } from '@tanstack/react-query';
import ProjectFolderAPI from '@/api/project_folder_api';
import { projectFolderQueryKeys } from './queries';
import { useTypedSelector } from '@/hooks/useTypedSelector';
import { ProjectFolder } from '@clef/shared/types';
import { projectQueryKeys } from '@/serverStore/projects';

export const useCreateProjectFolderMutation = () => {
  const queryClient = useQueryClient();
  const orgId = useTypedSelector(state => state.login.user?.orgId);
  const userId = useTypedSelector(state => state.login.user?.id);

  return useMutation({
    mutationFn: async ({
      name,
      parentFolderId,
    }: {
      name: string;
      parentFolderId?: number | null;
    }) => {
      const res = await ProjectFolderAPI.create(name, parentFolderId);
      return res.data;
    },
    onMutate: async ({ name, parentFolderId }) => {
      const queryKey = projectFolderQueryKeys.list(orgId ?? -1);
      await queryClient.cancelQueries({ queryKey });
      const prevData = queryClient.getQueryData<ProjectFolder[]>(queryKey);

      // Optimistically add the new folder with a temporary negative ID
      const tempFolder: ProjectFolder = {
        id: -Date.now(),
        orgId: orgId ?? -1,
        parentFolderId: parentFolderId ?? null,
        name,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdById: userId ?? null,
        deletedAt: null,
      };

      queryClient.setQueryData<ProjectFolder[]>(queryKey, old => [...(old ?? []), tempFolder]);

      return { prevData };
    },
    onError: (_err, _params, context) => {
      if (context?.prevData) {
        queryClient.setQueryData(projectFolderQueryKeys.list(orgId ?? -1), context.prevData);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: projectFolderQueryKeys.list(orgId ?? -1) });
    },
  });
};

export const useUpdateProjectFolderMutation = () => {
  const queryClient = useQueryClient();
  const orgId = useTypedSelector(state => state.login.user?.orgId);

  return useMutation({
    mutationFn: async ({ id, name }: { id: number; name: string }) => {
      const res = await ProjectFolderAPI.update(id, name);
      return res.data;
    },
    onMutate: async ({ id, name }) => {
      const queryKey = projectFolderQueryKeys.list(orgId ?? -1);
      await queryClient.cancelQueries({ queryKey });
      const prevData = queryClient.getQueryData<ProjectFolder[]>(queryKey);

      queryClient.setQueryData<ProjectFolder[]>(queryKey, old =>
        old?.map(folder =>
          folder.id === id ? { ...folder, name, updatedAt: new Date().toISOString() } : folder,
        ),
      );

      return { prevData };
    },
    onError: (_err, _params, context) => {
      if (context?.prevData) {
        queryClient.setQueryData(projectFolderQueryKeys.list(orgId ?? -1), context.prevData);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: projectFolderQueryKeys.list(orgId ?? -1) });
    },
  });
};

export const useDeleteProjectFolderMutation = () => {
  const queryClient = useQueryClient();
  const orgId = useTypedSelector(state => state.login.user?.orgId);

  return useMutation({
    mutationFn: async (id: number) => {
      await ProjectFolderAPI.deleteFolder(id);
      return id;
    },
    onMutate: async id => {
      const queryKey = projectFolderQueryKeys.list(orgId ?? -1);
      await queryClient.cancelQueries({ queryKey });
      const prevData = queryClient.getQueryData<ProjectFolder[]>(queryKey);

      queryClient.setQueryData<ProjectFolder[]>(queryKey, old =>
        old?.filter(folder => folder.id !== id),
      );

      return { prevData };
    },
    onError: (_err, _params, context) => {
      if (context?.prevData) {
        queryClient.setQueryData(projectFolderQueryKeys.list(orgId ?? -1), context.prevData);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: projectFolderQueryKeys.list(orgId ?? -1) });
    },
  });
};

export const useMoveProjectFolderMutation = () => {
  const queryClient = useQueryClient();
  const orgId = useTypedSelector(state => state.login.user?.orgId);

  return useMutation({
    mutationFn: async ({ id, parentFolderId }: { id: number; parentFolderId: number | null }) => {
      const res = await ProjectFolderAPI.move(id, parentFolderId);
      return res.data;
    },
    onMutate: async ({ id, parentFolderId }) => {
      const queryKey = projectFolderQueryKeys.list(orgId ?? -1);
      await queryClient.cancelQueries({ queryKey });
      const prevData = queryClient.getQueryData<ProjectFolder[]>(queryKey);

      queryClient.setQueryData<ProjectFolder[]>(queryKey, old =>
        old?.map(folder =>
          folder.id === id
            ? { ...folder, parentFolderId, updatedAt: new Date().toISOString() }
            : folder,
        ),
      );

      return { prevData };
    },
    onError: (_err, _params, context) => {
      if (context?.prevData) {
        queryClient.setQueryData(projectFolderQueryKeys.list(orgId ?? -1), context.prevData);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: projectFolderQueryKeys.list(orgId ?? -1) });
    },
  });
};

export const useMoveProjectToFolderMutation = () => {
  const queryClient = useQueryClient();
  const orgId = useTypedSelector(state => state.login.user?.orgId);

  return useMutation({
    mutationFn: async ({
      projectId,
      projectFolderId,
    }: {
      projectId: number;
      projectFolderId: number | null;
    }) => {
      await ProjectFolderAPI.moveProject(projectId, projectFolderId);
      return { projectId, projectFolderId };
    },
    onMutate: async ({ projectId, projectFolderId }) => {
      const queryKey = projectQueryKeys.list(orgId ?? -1);
      await queryClient.cancelQueries({ queryKey });
      const prevData = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old: any[] | undefined) =>
        old?.map(project => (project.id === projectId ? { ...project, projectFolderId } : project)),
      );

      return { prevData };
    },
    onError: (_err, _params, context) => {
      if (context?.prevData) {
        queryClient.setQueryData(projectQueryKeys.list(orgId ?? -1), context.prevData);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: projectQueryKeys.list(orgId ?? -1) });
    },
  });
};
